// src/locales/en/report.js
export const report = {
  // Page Title
  salesReport: 'Sales Report',
  salesReportSubtitle: 'Analyze sales data through interactive charts and detailed tables',
  
  // Filter Section
  filters: 'Filters',
  applyFilters: 'Apply Filters',
  resetFilters: 'Reset Filters',
  applying: 'Applying...',
  
  // Date Range
  dateRange: 'Date Range',
  dateFrom: 'From Date',
  dateTo: 'To Date',
  selectDateRange: 'Select Date Range',
  today: 'Today',
  thisWeek: 'This Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  thisYear: 'This Year',
  customRange: 'Custom Range',

  // Shared achieved/target labels (used by chart datasets)
  achieved: 'Achieved',
  target: 'Target',

  // Grouping
  grouping: 'Grouping',
  groupByDate: 'Group by Date',
  groupByWeek: 'Group by Week',
  groupByMonth: 'Group by Month',
  
  // Filter By Date Type
  filterByDateType: 'Filter By Date Type',
  createdDate: 'Created Date',
  dispatchedDate: 'Dispatched Date',
  
  // Shop Filter
  shop: 'Shop',
  allShops: 'All Shops',
  selectShops: 'Select shops',
  selectAll: 'Select all',
  deselectAll: 'Deselect all',
  selectAtLeastOneShop: 'Please select at least one shop',
  paymentType: 'Payment Type',
  selectPaymentTypes: 'Select payment types',
  salesRep: 'Sales Representative',
  allSalesReps: 'All sales representatives',
  groupBySalesRep: 'Show breakdown by sales representative',
  salesRepBreakdown: 'Sales by Representative',
  unassignedSalesRep: 'Unassigned',
  selectAtLeastOnePaymentType: 'Please select at least one payment type',
  excludeDeliveryCharge: 'Exclude delivery charge from sales value',
  deliveryChargeSettingSaved: 'Sales value setting updated',
  deliveryChargeSettingFailed: 'Failed to update sales value setting',
  selectAtLeastOneChannel: 'Please select at least one channel',
  channel: 'Channel',
  channelOnline: 'Online',
  channelOffline: 'Offline',
  channelPos: 'POS',
  statusAppliesToOnline: 'Status filter applies to online orders only',
  selectShop: 'Select Shop',
  
  // Status Filter
  orderStatus: 'Order Status',
  allStatuses: 'All Dispatched Statuses',
  selectStatuses: 'Select Statuses',
  
  // View Type
  viewType: 'View Type',
  timePeriodView: 'Time Period View',
  productView: 'Product View',
  
  // Summary Cards
  totalOrders: 'Total Orders',
  totalRevenue: 'Total Revenue',
  totalQuantity: 'Total Quantity',
  orders: 'orders',
  items: 'items',
  
  // Chart Section
  chartVisualization: 'Chart Visualization',
  revenue: 'Revenue',
  quantity: 'Quantity',
  orderCount: 'Order Count',
  noDataAvailable: 'No data available for the selected filters',
  
  // Table Section
  dataTable: 'Data Table',
  period: 'Period',
  productName: 'Product Name',
  productType: 'Product Type',
  quantitySold: 'Quantity Sold',
  totalValue: 'Total Value',
  actions: 'Actions',
  viewDetails: 'View Details',
  
  // Product Types
  simple: 'Simple',
  variable: 'Variable',
  
  // Detailed View
  orderNumber: 'Order Number',
  orderDate: 'Order Date',
  shopName: 'Shop',
  status: 'Status',
  quantity: 'Quantity',
  value: 'Value',
  
  // Order Items
  variationDetails: 'Variation Details',
  lineTotal: 'Line Total',
  
  // Variations
  variationSku: 'SKU',
  attributeCombination: 'Attributes',
  
  // Pagination
  showing: 'Showing',
  to: 'to',
  of: 'of',
  results: 'results',
  page: 'Page',
  previous: 'Previous',
  next: 'Next',
  
  // Export
  export: 'Export',
  exportToExcel: 'Export to Excel',
  exportToCsv: 'Export to CSV',
  exportChart: 'Export Chart',
  exporting: 'Exporting...',
  exportSuccess: 'Export completed successfully',
  exportFailed: 'Export failed',
  
  // Empty States
  noOrdersFound: 'No orders found',
  noOrdersMessage: 'No orders match the selected filters. Try adjusting your filter criteria.',
  
  // Validation
  dateFromRequired: 'Start date is required',
  dateToRequired: 'End date is required',
  dateRangeInvalid: 'End date must be after start date',
  groupByDateRequired: 'Grouping is required',
  filterByDateRequired: 'Filter by date type is required',
  
  // Loading
  loadingFilters: 'Loading filters...',
  loadingData: 'Loading data...',
  
  // Errors
  failedToLoadFilters: 'Failed to load filter options',
  failedToLoadData: 'Failed to load sales data',
  tryAgain: 'Try Again',

  // ── Profit & Loss Report ────────────────────────────────────────────────────
  pnlReport: {
    // Page
    title: 'Profit & Loss Report',
    subtitle: 'Comprehensive income statement for your business',

    // Group by
    groupByLabel: 'View Mode',
    groupByNone: 'Summary',
    groupByMonth: 'Monthly',
    groupByQuarter: 'Quarterly',

    // Date presets (shared with sales report but scoped here too)
    thisQuarter: 'This Quarter',
    lastQuarter: 'Last Quarter',

    // Actions
    generateReport: 'Generate Report',
    generating: 'Generating...',
    exportExcel: 'Export to Excel',
    exporting: 'Exporting...',

    // Revenue section
    revenue: 'Revenue',
    ecomSales: 'Ecom Sales',
    realizedSales: 'Realized Sales',
    unrealizedSales: 'Unrealized Sales',
    salesReturns: 'Less: Sales Returns',
    posSales: 'POS Sales',
    lessPosReturns: 'Less: POS Returns',
    netRevenue: 'Net Revenue',

    // COGS section
    cogs: 'Cost of Goods Sold',
    openingStock: 'Opening Stock',
    addPurchases: 'Add: Purchases',
    lessClosingStock: 'Less: Closing Stock',
    cogsTotal: 'Cost of Goods Sold',
    posProductCost: 'POS Product Cost',

    // Profit lines
    grossProfit: 'Gross Profit',

    // Expenses section
    operatingExpenses: 'Operating Expenses',
    courierCharges: 'Courier Charges',
    posFees: 'POS Payment Fees',
    totalExpenses: 'Total Operating Expenses',

    // Net profit
    netProfit: 'Net Profit',
    netLoss: 'Net Loss',

    // Total column header
    total: 'Total',

    // Empty / loading states
    noData: 'No data available for the selected period',
    selectFiltersPrompt: 'Select your filters and click "Generate Report" to view the P&L statement',
    failedToLoad: 'Failed to load P&L report data',
    failedToLoadFilters: 'Failed to load filter options',
  },

  courierPerformance: {
    title: 'Courier Performance Report',
    subtitle: 'Analyze courier delivery performance with comprehensive metrics and detailed order tracking',
    
    // Group By Options
    groupBy: 'Group By',
    groupByRequired: 'Grouping is required',
    byDate: 'By Date',
    byWeek: 'By Week',
    byMonth: 'By Month',
    byShippingZone: 'By Shipping Zone',
    byDistrict: 'By District',
    byProvince: 'By Province',
    
    // Date Presets
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    last30Days: 'Last 30 Days',
    last3Months: 'Last 3 Months',
    customRange: 'Custom Range',
    
    // Filters
    courierAccount: 'Courier Account',
    allCouriers: 'All Couriers',
    
    // Summary Cards
    totalDelivered: 'Total Delivered',
    totalReturned: 'Total Returned',
    inProcess: 'In Process',
    in_process: 'In Process',
    grandTotal: 'Grand Total',
    deliveryRate: 'Delivery Rate',
    returnRate: 'Return Rate',
    inProcessRate: "In Process Rate",
    
    // Chart
    showByCount: 'Show by Count',
    showByValue: 'Show by Value',
    delivered: 'Delivered',
    returned: 'Returned',
    
    // Table
    orderDetails: 'Order Details',
    all: 'All',
    dispatchedDate: 'Dispatched Date',
    customerName: 'Customer Name',
    location: 'Location',
    shippingZone: 'Shipping Zone',
    trackingNumber: 'Tracking Number',
    amount: 'Amount',
    codAmount: 'COD Amount',
    deliveryStatus: 'Delivery Status',
    searchOrders: 'Search by order number, tracking number, or customer name',
    orders: 'orders',
    
    // Empty States
    noDataYet: 'No Data Yet',
    selectFilters: 'Select your filters and click "Apply Filters" to view courier performance data',
  },

  messageCenterSummary: {
    title: 'Message Center Summary',
    subtitle: 'Track message center shift performance and confirmed order metrics',
    kpiSectionTitle: 'Key Metrics',
    kpiSectionTooltip: 'Summary metrics for the selected date range',
    createdOrders: 'Created Orders',
    createdOrdersTooltip: 'Total orders created by the message center team during the selected period.',
    convertOrders: 'Convert Orders',
    convertOrdersTooltip: 'Orders that have been dispatched to the courier — a valid processing milestone — excluding orders still pending, on hold, or not yet confirmed.',
    deliveredOrders: 'Delivered Orders',
    deliveredOrdersTooltip: 'Orders confirmed as delivered — either fully closed and delivered internally, or marked delivered by the courier.',
    settledOrders: 'Settled Orders',
    settledOrdersTooltip: 'Delivered orders that have completed the settlement process.',
    achievedVsTarget: 'Achieved vs Target',
    achievedVsTargetTooltip: "Achieved sales value from dispatched orders, compared against the shift's target.",
    totalCommission: 'Total Commission',
    totalCommissionTooltip: 'Total commission earned by the message center team.',
    leader: 'Leader',
    createdProducts: 'Created Products',
    createdValue: 'Created Value',
    totalOrders: 'Total Orders',
    totalProducts: 'Total Products',
    totalValue: 'Total Value',
    targetValue: 'Target Value',
    commission: 'Commission',
    todayShiftTitle: "Today's Achievement — {{shiftTypeName}}",
    todayShiftTooltip: "Summary of this shift type's performance across the selected date range, including created orders, total products, order value, and target achievement percentage.",
    rangeShiftTitle: "Achievement — {{shiftTypeName}}",
    last7DaysTitle: 'Last 7 Days Achieved vs Target — {{shiftTypeName}}',
    last7DaysTooltip: 'Achieved sales values versus the set target over the last 7 days.',
    noShiftTypesFound: 'No shift types configured for this business yet',
    noDataYet: 'No Data Yet',
    selectFiltersPrompt: 'Select your filters and click "Apply Filters" to view message center summary data',
    failedToLoad: 'Failed to load message center summary data',
  },

  callCenterSummary: {
    title: 'Call Center Summary',
    subtitle: 'Track call center KPIs, agent performance, and cross-selling results',
    kpiSectionTitle: 'Key Metrics',
    kpiSectionTooltip: 'Summary metrics for the selected date range',
    createdOrderItems: 'Created Order Items',
    createdOrderItemsTooltip: 'Total number of products in created orders.',
    validOrderItems: 'Valid Order Items',
    validOrderItemsTooltip: 'Total number of products in valid/confirmed orders.',
    deliveredOrderItems: 'Delivered Order Items',
    deliveredOrderItemsTooltip: 'Cross-sold items on orders confirmed as delivered — either fully closed and delivered internally, or marked delivered by the courier.',
    settledOrderItems: 'Settled Order Items',
    settledOrderItemsTooltip: 'Total number of products in settled orders.',
    inCourier: 'In Courier',
    inCourierTooltip: 'Products currently in transit with courier partners.',
    returns: 'Returns',
    returnsTooltip: 'Products in returned or pending return orders.',
    otherOrderItems: 'Other Order Items',
    otherOrderItemsTooltip: 'Products in other order statuses (cancelled, hold, etc.).',
    discount: 'Discount',
    discountTooltip: 'Total discount amount applied to orders.',
    products: 'Products',
    orderValue: 'Order Value',
    targetValue: 'Target Value',
    commission: 'Commission',
    product: 'Product',
    count: 'Count',
    revenue: 'Revenue (LKR)',
    name: 'Name',
    remaining: 'Remaining',
    targetAchieved: 'Target Achieved',
    rank: 'Rank',
    confirmCount: 'Confirm Count',
    sales: 'Sales',
    todayAchievement: "Achievement (Selected Period)",
    todayAchievementTooltip: "Cross-selling performance for confirmed orders in the selected period — products, order value, target, discount, and commission.",
    periodLabel: "Period",
    last7DaysTitle: 'Last 7 Days Achieved vs Target',
    last7DaysTooltip: 'Call center achieved sales value versus target performance over the last 7 days.',
    crossSellingTitle: 'Most Cross-Selling Products',
    crossSellingTooltip: 'Top 10 best-selling products by order count for the selected period.',
    targetPerAgentTitle: 'Target per Agents',
    targetPerAgentTooltip: "Each agent's cross-selling target versus achieved value for the selected period.",
    agentPerformanceTitle: 'Agent Performance',
    agentPerformanceTooltip: 'Ranking of all agents by total confirmed-order sales value for the selected period (any order, not cross-sell specific).',
    noDataYet: 'No Data Yet',
    selectFiltersPrompt: 'Select your filters and click "Apply Filters" to view call center summary data',
    failedToLoad: 'Failed to load call center summary data',
  },
  productWiseSales: {
    title: 'Product Wise Sales',
    description: 'Units, amounts and discounts per product for dispatched orders',
    shops: 'Shops',
    allShops: 'All shops',
    dateRange: 'Dispatched Date',
    searchPlaceholder: 'Search products...',
    excel: 'Excel',
    pdf: 'PDF',
    noData: 'No product sales found for the selected filters',
    failedToLoad: 'Failed to load product wise sales',
    exportFailed: 'Failed to export report',
    cards: {
      orderCount: 'Total Order Count',
      orderAmount: 'Total Order Amount',
      orderDiscount: 'Total Order Discount',
      productCount: 'Total Product Count',
      productAmount: 'Total Product Amount',
      productDiscount: 'Total Product Discount',
    },
    columns: {
      productName: 'Title',
      itemCount: 'Item Count',
      amount: 'Amount',
      contribution: 'Contribution (%)',
      discount: 'Discount',
      amountAfterDiscount: 'Amount After Discount',
      amountPerItem: 'Amount per Item',
      discountPerItem: 'Discount per Item',
      discountPctPerItem: 'Discount % per Item',
    },
  },
  productWiseReturns: {
    title: 'Product Wise Returns',
    description: 'Units, amounts and discounts per product for returned orders',
    shops: 'Shops',
    allShops: 'All shops',
    dateRange: 'Return Date',
    allReturnSources: 'All return batches & notes',
    searchReturnSources: 'Search return batches & notes...',
    returnBatch: 'Return Batch',
    returnNote: 'Return Note',
    searchPlaceholder: 'Search products...',
    excel: 'Excel',
    pdf: 'PDF',
    noData: 'No product returns found for the selected filters',
    failedToLoad: 'Failed to load product wise returns',
    exportFailed: 'Failed to export report',
    cards: {
      orderCount: 'Total Return Order Count',
      orderAmount: 'Total Return Order Amount',
      orderDiscount: 'Total Return Order Discount',
      productCount: 'Total Returned Product Count',
      productAmount: 'Total Returned Product Amount',
      productDiscount: 'Total Returned Product Discount',
    },
    columns: {
      productName: 'Title',
      itemCount: 'Item Count',
      amount: 'Amount',
      contribution: 'Contribution (%)',
      discount: 'Discount',
      amountAfterDiscount: 'Amount After Discount',
      amountPerItem: 'Amount per Item',
      discountPerItem: 'Discount per Item',
      discountPctPerItem: 'Discount % per Item',
    },
  },
  ccPerformanceUsers: {
    selectUsers: "Select Users",
    title: "Users shown in report",
    hint: "Toggle the users to include. If none are selected, all users are shown.",
    selectAll: "Select All",
    clearAll: "Clear All",
    saved: "User selection saved",
    saveFailed: "Failed to save user selection",
    loadFailed: "Failed to load users",
    searchUsers: "Search users by name",
    noUsersFound: "No users found",
  },
};